import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import find from 'lodash/find';
import compact from 'lodash/compact';
import Catalyst from '@Services/Catalyst';

let usedFacets;

const relativeContextsEnum = {
  citations: 'citations',
  reactions: 'reactions',
  substances: 'substances',
  dpitems: 'bioactivities',
  tgitems: 'targets',
};

export const handleWrappedFactItemFields = (fields, values) => {
  if (isEmpty(fields)) {
    return;
  }
  fields.forEach((field) => {
    // eslint-disable-next-line no-param-reassign
    field.fromForm = true;
    values.push(field.value);
  });
};

export const handleWrappedFactItems = (factItem, values, resultsFactsCollection) => {
  const { id, fields, exist, structures } = factItem;
  // iterate over all facts and then iterate over all fields and extract values,
  // but skip forms which are inside current form
  if (id !== undefined && id !== 'wrapper') {
    handleWrappedFactItemFields(fields, values);
  }
  // FindAny
  if (isEmpty(fields) && exist === true) {
    // eslint-disable-next-line no-param-reassign
    factItem.fromForm = true;
    values.push('FindAny');
  }
  if (structures && structures.length) {
    structures[0].fromForm = true;
    values.push('structure');
    resultsFactsCollection.push(structures[0]);
  }
};

export const handleWrappedFacts = (fact, resultsFactsCollection, context, forms) => {
  const { displayName, facts } = fact;
  if (isEmpty(facts)) {
    return;
  }

  const values = [];

  facts.forEach((factItem) => {
    handleWrappedFactItems(factItem, values, resultsFactsCollection);
  });
  // some fields are filled inside form, hence build criteria with a format 'Form Name:context:value1:value2;'
  if (values.length) {
    // eslint-disable-next-line no-param-reassign
    forms.criteria += `${displayName} form:${context}:${values.join(':')};`;
  }
};

export const /**
   * @name getFactsRecursively
   * @methodOf CatalystHelper
   * @description get facts recursively
   */
  getFactsRecursively = (queryFacts, resultsFactsCollection, context, forms) => {
    if (isEmpty(queryFacts)) {
      return;
    }
    queryFacts.forEach((fact) => {
      const { id, displayName, structures, facts } = fact;
      // iterate over all forms
      if (id === 'wrapper' && displayName && facts) {
        handleWrappedFacts(fact, resultsFactsCollection, context, forms);
      }
      if (id !== undefined && id !== 'wrapper') {
        resultsFactsCollection.push(fact);
      }
      if (structures && structures.length && !structures[0].fromForm) {
        resultsFactsCollection.push(structures[0]);
        return;
      }
      getFactsRecursively(facts, resultsFactsCollection, context, forms);
    });
  };

/**
 * @name getAllFactsFromAdvancedSearch
 * @methodOf Catalyst.CatalystHelper
 * @description get all facts from advanced search
 */
export const getAllFactsFromAdvancedSearch = (queryFactItem, context, forms = { criteria: '' }) => {
  if (!queryFactItem) {
    return [];
  }

  const resultsFactsCollection = [];
  getFactsRecursively(queryFactItem.facts, resultsFactsCollection, context, forms);
  return resultsFactsCollection;
};

/**
 * @name getCriteriaFromAdvancedSearch
 * @methodOf Catalyst.CatalystHelper
 * @description get criteria from advanced search
 */
export const getCriteriaFromAdvancedSearch = (queryFactItem, context) => {
  const forms = { criteria: '' };
  const facts = getAllFactsFromAdvancedSearch(queryFactItem, context, forms);
  const values = [];

  facts.forEach((fact) => {
    const { fields, exist, id, fromForm } = fact;

    if (!isEmpty(fields)) {
      fields.forEach((field) => {
        if (field.fromForm) {
          return;
        }
        values.push(field.value);
      });
    }

    // FindAny
    if (isEmpty(fields) && exist === true && !fromForm) {
      values.push('FindAny');
    }

    // structures
    if (id === 'STRUCTURE.QL' && !fromForm) {
      values.push('structure');
    }
  });

  if (!values.length) {
    return forms.criteria;
  }
  // context criteria (info RXFE-5259)
  return `${forms.criteria}${context}:${values.join(':')}`;
};

/**
 * @name getDataFormCriteriaFromAdvancedSearch
 * @methodOf Catalyst.CatalystHelper
 * @description get data from criteria from advanced search
 */
export const getDataFormCriteriaFromAdvancedSearch = (queryFactItem, context) => {
  const facts = getAllFactsFromAdvancedSearch(queryFactItem);
  const criteriaItems = [];

  // facts dataFormCriteria
  facts.forEach((fact) => {
    const { fields, exist, id } = fact;

    if (!isEmpty(fields)) {
      fields.forEach((field) => {
        const criteriaItem = `${field.id}:${field.boundOperator}:${field.value}`;
        criteriaItems.push(criteriaItem);
      });
    }

    // FindAny
    if (isEmpty(fields) && exist === true) {
      criteriaItems.push(`${id}:exist:true`);
    }

    // structures
    if (id === 'STRUCTURE.QL') {
      criteriaItems.push('structure');
    }
  });

  return `${context}?advanced=${criteriaItems.join('&')}`;
};

/**
 * @name getResultsByType
 * @methodOf Catalyst.CatalystHelper
 * @description get results by type
 * RXFE-7388 - not be using for time being
 */
export const getResultsByType = (context, resultSize, relativeContexts) => {
  const results = [];

  results.push({
    name: context,
    results: resultSize.toString(),
  });

  const keys = Object.keys(relativeContextsEnum);
  keys.forEach((key) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!relativeContextsEnum.hasOwnProperty(key) || !relativeContexts[key]) {
      return;
    }
    results.push({
      name: relativeContextsEnum[key], // dpitems -> bioactivities, tgitems -> targets
      results: relativeContexts[key].resultSize.toString(),
    });
  });

  return results;
};

/**
 * @name getSizeOrZero
 * @methodOf Catalyst.CatalystHelper
 * @description get size or zero
 */
export const getSizeOrZero = (context) => {
  return get(context, 'resultSize', 0);
};

/**
 * @name getTotalResults
 * @methodOf Catalyst.CatalystHelper
 * @description get total results
 * RXFE-7388 - not be using for time being
 */
export const getTotalResults = (relativeContexts, resultSize) => {
  let totalResults = resultSize;
  const keys = Object.keys(relativeContextsEnum);
  keys.forEach((key) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!relativeContextsEnum.hasOwnProperty(key)) {
      return;
    }
    totalResults += getSizeOrZero(relativeContexts[key]);
  });

  return totalResults;
};

/**
 * @name getFacetGroups
 * @methodOf Catalyst.CatalystHelper
 * @description get facet groups (selectedTaxGroups from hierarchy or groups filters)
 */
export const getFacetGroups = (filterItem) => {
  if (filterItem.selectedTaxGroups) {
    const mappedSelectedTaxGroups = filterItem.selectedTaxGroups.map((item) => item.name || item.title);
    return compact(mappedSelectedTaxGroups);
  }

  const groups = get(filterItem, 'groups', []);
  const mappedGroups = groups.map((item) => {
    if (!item.isSelected) {
      return;
    }
    // eslint-disable-next-line consistent-return
    return item.title;
  });

  return compact(mappedGroups);
};

/**
 * @name setFacetNameAndValues
 * @methodOf Catalyst.CatalystHelper
 * @description get facet name and values
 */
export const setFacetNameAndValues = (facets, filterItem) => {
  const { title, filterUserValue, groups, selectedTaxGroups } = filterItem;
  if (find(facets, { name: title })) {
    return;
  }

  if (filterUserValue) {
    facets.push({
      name: title,
      values: [filterUserValue],
      isFilterUserValue: true,
    });
    return;
  }
  if (groups || selectedTaxGroups) {
    facets.push({
      name: title,
      values: getFacetGroups(filterItem),
    });
    return;
  }
  facets.push({ name: title, values: [`selected-${new Date().valueOf()}`] });
};

/**
 * @name setFacets
 * @methodOf Catalyst.CatalystHelper
 * @param {Array} facets
 * @description set used facets
 * */
export const setFacets = (facets) => {
  usedFacets = facets;
};

/**
 * @name getFacets
 * @methodOf Catalyst.CatalystHelper
 * @description get used facets
 * */
export const getFacets = () => {
  return usedFacets;
};

/**
 * @name setTreeNodeTrackingSearchType
 * @methodOf Catalyst.CatalystHelper
 * @description Analytics - set search type for used querylets
 * */
export const setTreeNodeTrackingSearchType = (item, tab, parentItem) => {
  // if Array:
  // [0] - used for "combination" case
  // [1] - actual section name
  // [2] - if true - it shouldn't check children recursively

  const { displayName } = item;
  if (tab === 'forms') {
    // eslint-disable-next-line no-param-reassign
    item.trackingSearchType = parentItem ? ['forms', `${parentItem.displayName}:${displayName}`] : 'forms';
    return;
  }
  if (tab === 'qsforms') {
    // eslint-disable-next-line no-param-reassign
    item.trackingSearchType = [`${tab}(${displayName})`, `${tab}:${displayName}`];
    return;
  }
  // eslint-disable-next-line no-param-reassign
  item.trackingSearchType = tab;
};

/**
 * @name generateTrackedObjFromNodesList
 * @methodOf Catalyst.CatalystHelper
 * @description Analytics - generate search type from used querylets
 * */
export const generateTrackedObjFromNodesList = (nodesList, context) => {
  const searchTypeArr = [Catalyst.getPrefix(), 'explicit', 'querybuilder', context];

  let trackedTypes = [];

  const recursive = (item) => {
    if (isEmpty(item)) {
      return;
    }
    const { trackingSearchType, items } = item;
    if (trackingSearchType) {
      trackedTypes.push(trackingSearchType);
    }
    if (!isEmpty(items)) {
      items.forEach(recursive);
    }
  };

  if (!isEmpty(nodesList)) {
    nodesList.forEach(recursive);
  }

  // if no items with trackingSearchType
  if (isEmpty(trackedTypes)) {
    return null;
  }

  // reduce to only unique types
  trackedTypes = _.uniq(trackedTypes, (val) => {
    return isArray(val) ? val[1] : val;
  });

  if (trackedTypes.length > 1) {
    // if combination
    searchTypeArr.push('combination');
    trackedTypes = trackedTypes.map((val) => (isArray(val) ? val[0] : val));

    searchTypeArr.push(trackedTypes.join('+'));
  } else {
    // if just single type is used
    const type = trackedTypes[0];
    searchTypeArr.push(isArray(type) ? type[1] : type);
  }

  return {
    type: searchTypeArr.join(':'),
  };
};
